import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { Contact } from "../../../../api/types/fundStructureTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  onDeleted: (contactId: string) => void;
  contact: Contact;
}

const deleteContact = withErrorHandling(adminApi.deleteContact);

const DeleteContactDialog = ({ open, onClose, onDeleted, contact }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    const [resp, error] = await deleteContact(contact.id);
    setIsDeleting(false);

    if (error || !resp.isDeleted) {
      logError(error, "[DeleteContactDialog]");
      sendNotificationError(resp?.displayErrorMessage || "Failed to delete contact");
      return;
    }

    sendNotification(`Contact ${contact.name} deleted`);
    onDeleted(contact.id);
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete contact?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete <strong>{contact.name}</strong>. This action is permanent and can&apos;t be
              undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={isDeleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteContactDialog;
